import { useQuery } from '@tanstack/react-query';
import { useApi } from './api';
import { useRegistryMutation } from './queries';
import { useSyndicate } from './session';

export interface ReminderItem {
  id: string;
  name: string;
  email: string;
  language: 'fr' | 'en';
  stage: 1 | 2;
  units: { unitId: string; label: string; balanceCents: number }[];
  subject: string;
  text: string;
}

export interface RemindersPayload {
  asOf: string;
  items: ReminderItem[];
  escalations: { unitId: string; label: string; balanceCents: number; owners: string }[];
  unreachable: { unitId: string; label: string; owners: string }[];
  unreconciledDeposits: number;
}

export interface Approval {
  id: string;
  kind: string;
  title: string;
  payload: RemindersPayload;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedBy: string | null;
  decidedAt: string | null;
  decision: { outcome: string; note?: string; excludedItemIds?: string[]; result?: { sent: number; skippedPaid: string[]; escalated: number } } | null;
  createdAt: string;
  canDecide?: boolean;
}

export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';

export function useApprovals(status: 'pending' | 'all' = 'pending') {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'approvals', status],
    queryFn: () => api<Approval[]>(`${s.api}/approvals?status=${status}`),
    enabled: s.can('collections.prepare') || s.can('collections.approve'),
  });
}

export function useApproval(id: string) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'approvals', 'one', id], queryFn: () => api<Approval>(`${s.api}/approvals/${id}`) });
}

export function useCollectionsStatus() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'approvals', 'collections-status'],
    queryFn: () => api<{ unitId: string; stage: number; lastActionAt: string | null }[]>(`${s.api}/collections/status`),
    enabled: s.can('ledger.read'),
  });
}

/** Writes that change approvals; refreshes approvals, emails and the books. */
export function useApprovalMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  return useRegistryMutation<Input, Output>((api, registryBase, input) => request(api, registryBase.replace(/\/registry$/, ''), input), [
    'approvals',
    'comms',
    'ledger',
  ]);
}
